import { css } from 'styled-components'
import { getTextStyleCss } from '../../core-components/text/helpers'
import { ThemeColors, ThemeShadows } from '../../theme'

const autofill = ({
  textColor,
  bgColor,
  shadow,
}: {
  textColor?: ThemeColors
  bgColor?: ThemeColors
  shadow?: ThemeShadows
}) => css`
  :-webkit-autofill {
    ${textColor
      ? css`
          -webkit-text-fill-color: ${({ theme }) => theme.colors[textColor]};
        `
      : undefined}
    ${bgColor
      ? css`
          -webkit-box-shadow: ${({ theme }) => {
            const shadows = [
              shadow ? theme.shadows[shadow] : undefined,
              `0 0 0 1000px ${theme.colors[bgColor]} inset`,
              `0 0 0 1000px ${theme.colors['bg/section']} inset`,
            ].filter(shadow => shadow !== undefined)
            return shadows.join(', ')
          }};
          transition: background 0.2s ease-in-out; // Remove box shadow transition because it goes crazy with this hack when animation to or out of the shadow we use for focus state
        `
      : undefined}
  }
`

const getBackgroundColor = (hasError: boolean, variant: 'regular' | 'ghost' | 'nested') => {
  if (variant === 'ghost' || variant === 'nested') {
    return 'transparent'
  }
  if (hasError) {
    return 'bg/form-item-error'
  }
  return 'bg/form-item-default'
}

const getBoxShadow = (hasError: boolean, variant: 'regular' | 'ghost' | 'nested') => {
  if (hasError || variant === 'ghost' || variant === 'nested') {
    return 'none'
  }
  return 'border/section'
}

export const sharedInputContainerCss = css<{
  hasError: boolean
  variant: 'regular' | 'ghost' | 'nested'
}>`
  ${({ theme, hasError, variant }) => {
    const bgColor = getBackgroundColor(hasError, variant)
    const shadow = getBoxShadow(hasError, variant)

    return css`
      background: ${theme.colors[bgColor]};
      box-shadow: ${theme.shadows[shadow]};
    `
  }};
  border-radius: ${({ theme, variant }) => (variant === 'nested' ? undefined : theme.radii[10])};
  transition:
    box-shadow 0.2s ease-in-out,
    background 0.2s ease-in-out;

  :hover {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
    box-shadow: none;
  }

  :focus-within {
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};
    box-shadow: ${({ theme, variant }) =>
      theme.shadows[
        variant === 'nested' ? 'border/form-item-nested-focused' : 'border/form-item-focused'
      ]};
  }

  :disabled,
  :has(input:disabled, textarea:disabled) {
    background: ${({ theme }) => theme.colors['bg/disabled']};
    box-shadow: none;
  }
`

export const sharedInputCss = css<{ hasError: boolean; variant: 'regular' | 'ghost' | 'nested' }>`
  ${({ theme, hasError, variant }) => {
    const textColor: ThemeColors = 'fg/major'
    const bgColor = getBackgroundColor(hasError, variant)
    const shadow = getBoxShadow(hasError, variant)

    return css`
      color: ${theme.colors[textColor]};

      ${autofill({ textColor, bgColor, shadow })}
    `
  }};
  border: none;
  background: transparent;

  ${getTextStyleCss('form-value')}

  ::placeholder {
    ${getTextStyleCss('form-value')}
    color: ${({ theme }) => theme.colors['fg/minor']};
  }

  :hover {
    ${autofill({ bgColor: 'bg/form-item-hovered' })}
  }
  :focus {
    ${({ variant }) =>
      autofill({
        bgColor: 'bg/form-item-focused',
        shadow:
          variant === 'nested' ? 'border/form-item-nested-focused' : 'border/form-item-focused',
      })}
    outline: none;
  }
  :disabled {
    color: ${({ theme }) => theme.colors['fg/disabled']};
    ${autofill({ textColor: 'fg/disabled', bgColor: 'bg/disabled' })}

    ::placeholder {
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }
  }
`
